export type MediaLibraryItem = {
  id: number;
  original_name: string;
  stored_name: string;
  mime_type: string;
  size_bytes: number;
  size_label: string;
  width: number | null;
  height: number | null;
  dimensions_label: string;
  alt_text: string;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
  usage_count: number;
  url_path: string;
  public_url: string;
};

export type MediaStorageQuota = {
  limit_bytes: number | null;
  limit_label: string | null;
  used_bytes: number;
  used_label: string;
  remaining_bytes: number | null;
  remaining_label: string | null;
  usage_ratio: number | null;
  usage_percent: number | null;
  warning_percent: number;
  status: "ok" | "warning" | "full" | "unlimited";
};

export type MediaLibraryStats = {
  item_count: number;
  total_size_bytes: number;
  total_size_label: string;
  last_imported_at: string | null;
  last_used_at: string | null;
  quota: MediaStorageQuota;
};

export type MediaLibraryResponse = {
  items: MediaLibraryItem[];
  stats: MediaLibraryStats;
};

export type MediaImportResult = {
  item: MediaLibraryItem;
  message: string;
};

export type MediaUsagePayload = {
  source_app: string;
  usage_context: string;
  usage_label?: string;
};

type MediaApiError = Error & {
  fieldErrors?: string[];
};

type MediaUploadPreparation = {
  object_key: string;
  upload_url: string;
  public_url: string;
};

// Source uploads are bounded separately from the compressed 3 MiB stored image.
const MEDIA_UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
const MEDIA_UPLOAD_RECOMMENDED_BYTES = 1024 * 1024;
const MEDIA_UPLOAD_MAX_DIMENSION_PX = 2400;

function buildJsonHeaders(init: RequestInit) {
  const headers = new Headers(init.headers || undefined);
  const hasBody = init.body !== undefined && init.body !== null;
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  const isUrlSearchParams =
    typeof URLSearchParams !== "undefined" && init.body instanceof URLSearchParams;
  const isBlob = typeof Blob !== "undefined" && init.body instanceof Blob;

  if (hasBody && !headers.has("Content-Type") && !isFormData && !isUrlSearchParams && !isBlob) {
    headers.set("Content-Type", "application/json");
  }

  return headers;
}

function readMediaBaseUrlOverride() {
  if (typeof window === "undefined") {
    return "";
  }
  const override = (window as Window & { __EASY_MEDIA_BASE_URL__?: string }).__EASY_MEDIA_BASE_URL__;
  return typeof override === "string" ? override.trim() : "";
}

function buildStandaloneMediaBaseUrl() {
  if (typeof window === "undefined") {
    return "http://127.0.0.1:8800";
  }
  return `${window.location.protocol}//${window.location.hostname}:8800`;
}

function isHostedMediaRoute(pathname: string) {
  return (
    pathname.startsWith("/app/") ||
    /^\/(events|contacts|media|newsletter|social|settings|billing)(\/|$)/.test(pathname)
  );
}

export function getMediaServiceBaseUrl() {
  if (typeof window === "undefined") {
    return "http://127.0.0.1:8790";
  }

  const override = readMediaBaseUrlOverride();
  if (override) {
    return override.replace(/\/+$/, "");
  }

  if (window.location.pathname.startsWith("/apps/")) {
    return `${window.location.origin}/apps/media`;
  }

  if (isHostedMediaRoute(window.location.pathname)) {
    return window.location.origin;
  }

  return buildStandaloneMediaBaseUrl();
}

function mediaApiUrl(path: string) {
  const normalizedPath = String(path || "").replace(/^\/+/, "");
  return `${getMediaServiceBaseUrl()}/api/${normalizedPath}`.replace(/\/{2,}/g, "/").replace(":/", "://");
}

async function fetchMediaJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: buildJsonHeaders(init),
  });

  return readMediaResponse<T>(response);
}

async function readMediaResponse<T>(response: Response): Promise<T> {
  if (response.status === 413) {
    throw new Error("Le serveur a refusé cette image car elle est trop volumineuse. La limite d’import est de 20 Mo avant compression.");
  }
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(payload?.error || payload?.detail || "L’import a échoué. Réessayez dans quelques instants.") as MediaApiError;
    error.fieldErrors = payload?.field_errors || [];
    throw error;
  }
  return payload as T;
}

function formatMediaSizeLabel(sizeBytes: number) {
  if (sizeBytes < 1024) {
    return `${sizeBytes} o`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${Math.round(sizeBytes / 1024)} Ko`;
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(1).replace(".", ",")} Mo`;
}

export async function listMediaLibrary() {
  return fetchMediaJson<MediaLibraryResponse>(mediaApiUrl("media"));
}

export async function importMediaFile(file: File, altText = ""): Promise<MediaImportResult> {
  if (!file.size) {
    throw new Error("Le fichier image est vide.");
  }
  if (file.size > MEDIA_UPLOAD_MAX_BYTES) {
    throw new Error("Le fichier source dépasse la limite de 20 Mo.");
  }
  const contentType = file.type || "application/octet-stream";
  const upload = await fetchMediaJson<MediaUploadPreparation>(mediaApiUrl("media/upload-url"), {
    method: "POST",
    body: JSON.stringify({ original_name: file.name, content_type: contentType }),
  });
  const uploadUrl = new URL(upload.upload_url, `${getMediaServiceBaseUrl()}/`).toString();
  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  await readMediaResponse(uploadResponse);
  const item = await fetchMediaJson<MediaLibraryItem & { optimization_summary?: string }>(mediaApiUrl("media/finalize"), {
    method: "POST",
    body: JSON.stringify({
      object_key: upload.object_key,
      original_name: file.name,
      content_type: contentType,
      size_bytes: file.size,
      alt_text: altText,
    }),
  });
  return {
    item,
    message: [item.optimization_summary, "Image importee avec succes."].filter(Boolean).join("\n"),
  };
}

export async function deleteMediaBulk(mediaIds: number[]) {
  return fetchMediaJson<{ deleted_count: number }>(mediaApiUrl("media/delete-bulk"), {
    method: "POST",
    body: JSON.stringify({
      media_ids: mediaIds,
    }),
  });
}

export async function recordMediaUsage(mediaId: number, payload: MediaUsagePayload) {
  return fetchMediaJson<{ item: MediaLibraryItem; message: string }>(mediaApiUrl(`media/${mediaId}/usage`), {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function formatMediaDateTime(value?: string | null) {
  if (!value) {
    return "Jamais";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

export function hasMediaStorageLimit(quota?: MediaStorageQuota | null) {
  return typeof quota?.limit_bytes === "number" && quota.limit_bytes > 0;
}

export {
  MEDIA_UPLOAD_MAX_BYTES,
  MEDIA_UPLOAD_MAX_DIMENSION_PX,
  MEDIA_UPLOAD_RECOMMENDED_BYTES,
  formatMediaSizeLabel,
};
